'use client'

import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { CttCampaign } from './trustCenterTypes'
import { parseDbTimestamp } from '@/lib/dates'

export function TrustCenterBrandCard({
  entry,
  theme,
  onClick,
  isSelected,
}: {
  entry: CttCampaign
  theme: ProcessedTheme
  onClick?: () => void
  isSelected?: boolean
}) {
  const c = watchClasses(theme)
  const dateLabel = compactDate(entry.created_at)
  const dayMonth = (() => {
    const d = parseDbTimestamp(entry.created_at)
    if (!d) return '—'
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
  })()
  const dark = theme === 'dark'

  return (
    <article
      className={`border-b transition-colors cursor-pointer ${c.line} ${isSelected ? (dark ? 'bg-signal-surface border-l-2 border-l-signal' : 'bg-signal-surface border-l-2 border-l-signal') : dark ? 'hover:bg-[#121a21]' : 'hover:bg-secondary'}`}
      onClick={onClick}
    >
      <div className="grid w-full grid-cols-[1fr_auto] gap-6 px-7 py-6">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${c.line} ${c.soft}`}>
              <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Brand</span>
              <span className={`font-mono text-[10px] ${c.muted}`}>{entry.id.slice(0, 8)}…</span>
            </span>
            {entry.processed_entry_id
              ? <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${dark ? 'bg-indigo-950/30 border-indigo-700 text-indigo-400' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}>Brand · Processed</span>
              : <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${dark ? 'bg-signal-surface border-signal/30 text-signal' : 'bg-signal-surface border-signal/30 text-signal'}`}>Brand</span>
            }
            <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${entry.is_active ? (dark ? 'bg-success-surface border-success/40 text-success' : 'bg-success-surface border-success/40 text-success') : (dark ? 'bg-muted border-border text-muted-foreground' : 'bg-secondary border-border text-muted-foreground')}`}>
              {entry.is_active ? 'active' : 'inactive'}
            </span>
            <span className={`text-xs ${c.muted}`}>first seen {dateLabel}</span>
          </div>
          <h2 className={`mt-4 text-xl font-semibold leading-snug tracking-[-0.01em] ${c.text}`}>
            "{entry.headline}"
          </h2>
          {entry.description && (
            <p className={`mt-3 max-w-4xl text-[17px] leading-7 ${c.muted}`}>
              {entry.description}
            </p>
          )}
          {entry.image_count > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${c.line} ${c.soft}`}>
                {entry.image_count} image{entry.image_count !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
        <div className="hidden min-w-[120px] text-right md:block">
          <div className={`font-mono text-[34px] leading-none tracking-tight ${c.text}`}>{dayMonth}</div>
          <div className={`mt-2 font-mono text-[10px] uppercase tracking-[0.3em] ${c.label}`}>published</div>
        </div>
      </div>
    </article>
  )
}
